import Phaser from 'phaser';
import { tileToScreen, isoDepth } from './iso';
import type { Chunk } from './chunk';
import { findPath, type Point } from './pathfinding';
import { bandFor, DEFAULT_PROFILE, PROFILES, type MoraleProfile } from './morale';
import type { ActiveStatus } from './abilities';
import { ITEMS, type EquipSlot, type StatMods } from './items';

export type Faction = 'party' | 'enemy';

export type WeaponKind = 'slash' | 'bludgeon' | 'pierce' | 'magic';

export interface UnitDef {
  id: string;          // unique runtime id
  name: string;
  spriteKey: string;
  faction: Faction;
  maxHp: number;
  attackDamage: number;
  attackRange: number;  // in tiles
  attackInterval: number; // seconds
  moveSpeed: number;    // tiles/sec
  weapon: WeaponKind;
  sightRange?: number;
  tint?: number;        // optional Phaser color tint for the sprite
  role?: 'vanguard' | 'marksman' | 'medic' | 'mage' | 'controller' | 'skirmisher';
}

interface UnitOpts {
  scene: Phaser.Scene;
  def: UnitDef;
  tileX: number;
  tileY: number;
  originX: number;
  originY: number;
}

/**
 * Live actor — player party or enemy. Owns its sprite, hp bar, and movement
 * state. The Scene drives ticks and combat resolution.
 */
export class Unit {
  readonly def: UnitDef;
  hp: number;
  tileX: number;
  tileY: number;
  progress = 0;
  path: Point[] = [];
  sprite: Phaser.GameObjects.Image;
  hpBar: Phaser.GameObjects.Graphics;
  attackCooldown = 0;
  alive = true;
  target: Unit | null = null;
  morale: number;
  moraleProfile: MoraleProfile;
  moraleBadge: Phaser.GameObjects.Graphics | null = null;
  statuses?: ActiveStatus[];
  /** Pixel Y offset applied on top of tile position — set by the scene to
   *  account for terrain elevation at the current tile. */
  yOffset = 0;
  /** Equipped item IDs by slot. Mirrored from RosterEntry on rebuild. */
  equipment: Partial<Record<EquipSlot, string>> = {};
  // Cached chunk-tile of last replan so we don't recompute every frame.
  lastReplanFromX = -999;
  lastReplanFromY = -999;
  lastReplanToX = -999;
  lastReplanToY = -999;

  private originX: number;
  private originY: number;

  constructor(opts: UnitOpts) {
    this.def = opts.def;
    this.hp = opts.def.maxHp;
    this.tileX = opts.tileX;
    this.tileY = opts.tileY;
    this.originX = opts.originX;
    this.originY = opts.originY;
    // Look up morale profile by role first, then by spriteKey (enemy slug).
    const slug = opts.def.role ?? opts.def.spriteKey.replace(/^sprite_/, '');
    this.moraleProfile = PROFILES[slug] ?? DEFAULT_PROFILE;
    this.morale = this.moraleProfile.base;

    const { sx, sy } = tileToScreen(this.tileX, this.tileY);
    this.sprite = opts.scene.add.image(opts.originX + sx, opts.originY + sy - 18, opts.def.spriteKey);
    this.sprite.setDisplaySize(40, 40);
    this.sprite.setDepth(isoDepth(this.tileX, this.tileY, 100));
    if (opts.def.tint !== undefined) this.sprite.setTint(opts.def.tint);

    this.hpBar = opts.scene.add.graphics();
    this.hpBar.setDepth(isoDepth(this.tileX, this.tileY, 110));
    this.drawHpBar();
  }

  worldPos(): { x: number; y: number } {
    if (this.path.length === 0) {
      const { sx, sy } = tileToScreen(this.tileX, this.tileY);
      return { x: this.originX + sx, y: this.originY + sy + this.yOffset };
    }
    const next = this.path[0];
    const a = tileToScreen(this.tileX, this.tileY);
    const b = tileToScreen(next.x, next.y);
    return {
      x: this.originX + a.sx + (b.sx - a.sx) * this.progress,
      y: this.originY + a.sy + (b.sy - a.sy) * this.progress + this.yOffset,
    };
  }

  /** Aggregated stat mods from currently-equipped items. */
  equipmentMods(): StatMods {
    const out: StatMods = {};
    for (const id of Object.values(this.equipment)) {
      if (!id) continue;
      const it = ITEMS[id];
      if (!it) continue;
      for (const k of Object.keys(it.mods) as (keyof StatMods)[]) {
        out[k] = (out[k] ?? 0) + (it.mods[k] ?? 0);
      }
    }
    return out;
  }

  get effectiveMaxHp(): number {
    return Math.max(1, this.def.maxHp + (this.equipmentMods().maxHp ?? 0));
  }
  get effectiveAttackDamage(): number {
    return Math.max(1, this.def.attackDamage + (this.equipmentMods().attackDamage ?? 0));
  }
  get effectiveAttackRange(): number {
    return Math.max(1, this.def.attackRange + (this.equipmentMods().attackRange ?? 0));
  }
  get effectiveAttackInterval(): number {
    return Math.max(0.2, this.def.attackInterval + (this.equipmentMods().attackInterval ?? 0));
  }
  get effectiveMoveSpeed(): number {
    return Math.max(1.0, this.def.moveSpeed + (this.equipmentMods().moveSpeed ?? 0));
  }
  get armor(): number {
    return Math.max(0, this.equipmentMods().armor ?? 0);
  }

  /** Tile distance (Chebyshev) to another unit. */
  tileDistanceTo(other: Unit): number {
    return Math.max(Math.abs(this.tileX - other.tileX), Math.abs(this.tileY - other.tileY));
  }

  setPath(path: Point[]): void {
    // Preserve interpolation progress when the immediate next step is the same.
    // Without this, any frequently-re-planned unit (followers tracking a moving
    // leader, enemies chasing a moving player) sawtooths back to progress=0
    // every replan and never visibly advances.
    const sameNextStep =
      this.path.length > 0 &&
      path.length > 0 &&
      this.path[0].x === path[0].x &&
      this.path[0].y === path[0].y;
    this.path = path;
    if (!sameNextStep) this.progress = 0;
  }

  /** Plan a path toward target only when the goal tile has actually changed. */
  pathTo(chunk: Chunk, goalX: number, goalY: number): void {
    if (
      this.lastReplanFromX === this.tileX && this.lastReplanFromY === this.tileY &&
      this.lastReplanToX === goalX && this.lastReplanToY === goalY
    ) return;
    this.lastReplanFromX = this.tileX;
    this.lastReplanFromY = this.tileY;
    this.lastReplanToX = goalX;
    this.lastReplanToY = goalY;
    const p = findPath(chunk, { x: this.tileX, y: this.tileY }, { x: goalX, y: goalY });
    this.setPath(p);
  }

  tickMove(dt: number): void {
    if (this.path.length === 0) return;
    const next = this.path[0];
    const dx = next.x - this.tileX;
    const dy = next.y - this.tileY;
    const segLen = Math.hypot(dx, dy);
    // Slow status halves move speed.
    const slowed = this.statuses?.some(s => s.kind === 'slow') ? 0.5 : 1.0;
    this.progress += (this.effectiveMoveSpeed * slowed * dt) / Math.max(segLen, 0.0001);
    if (this.progress >= 1) {
      this.tileX = next.x;
      this.tileY = next.y;
      this.progress = 0;
      this.path.shift();
    }
  }

  tickCooldown(dt: number): void {
    if (this.attackCooldown > 0) this.attackCooldown = Math.max(0, this.attackCooldown - dt);
  }

  takeDamage(amount: number): void {
    // Armor flat reduction, minimum 1 damage on any hit.
    const mitigated = Math.max(1, amount - this.armor);
    this.hp = Math.max(0, this.hp - mitigated);
    if (this.hp === 0) this.alive = false;
    this.drawHpBar();
    // Heavy hit (>20% maxHp) shakes morale.
    if (mitigated >= this.effectiveMaxHp * 0.2) this.applyMoraleDelta(-8);
  }

  /** Apply a morale change scaled by this unit's resolve. Positive = boost. */
  applyMoraleDelta(delta: number): void {
    if (delta < 0 && this.moraleProfile.immunities.has('fear')) return;
    const scaled = delta < 0 ? delta * this.moraleProfile.resolve : delta;
    this.morale = Math.max(0, Math.min(100, this.morale + scaled));
  }

  /** Current morale band (cached lookup). */
  moraleBand() { return bandFor(this.morale); }

  /** True if this unit should rout and flee. */
  isRouted(): boolean {
    if (this.moraleProfile.immunities.has('rout')) return false;
    return this.morale < 10;
  }

  drawHpBar(): void {
    const { x, y } = this.worldPos();
    this.drawHpBarAt(x, y);
  }

  syncSprite(): void {
    const { x, y } = this.worldPos();
    this.sprite.setPosition(x, y - 18);
    this.sprite.setDepth(isoDepth(this.tileX, this.tileY, 100));
    this.hpBar.setDepth(isoDepth(this.tileX, this.tileY, 110));
    this.drawHpBarAt(x, y);
    this.drawMoraleBadgeAt(x, y);
  }

  private drawMoraleBadgeAt(x: number, y: number): void {
    if (!this.alive) {
      this.moraleBadge?.clear();
      return;
    }
    if (!this.moraleBadge) {
      this.moraleBadge = (this.sprite.scene).add.graphics();
    }
    this.moraleBadge.setDepth(isoDepth(this.tileX, this.tileY, 112));
    this.moraleBadge.clear();
    // Hide badge for default-steady units (less visual clutter).
    const band = this.moraleBand();
    if (band.state === 'steady' && this.morale > 60) return;
    // Small colored dot to the left of the HP bar.
    const cx = x - 24;
    const cy = y - 36;
    this.moraleBadge.fillStyle(0x000000, 0.7);
    this.moraleBadge.fillCircle(cx, cy, 4);
    this.moraleBadge.fillStyle(band.tint, 1);
    this.moraleBadge.fillCircle(cx, cy, 3);
  }

  private drawHpBarAt(x: number, y: number): void {
    this.hpBar.clear();
    if (!this.alive) return;
    const w = 36;
    const h = 4;
    const bx = x - w / 2;
    const by = y - 38;
    const ratio = this.hp / this.effectiveMaxHp;
    const color = this.def.faction === 'party' ? 0x4caf50 : 0xc62828;
    this.hpBar.fillStyle(0x000000, 0.7);
    this.hpBar.fillRect(bx - 1, by - 1, w + 2, h + 2);
    this.hpBar.fillStyle(color, 1);
    this.hpBar.fillRect(bx, by, w * ratio, h);
  }

  destroy(): void {
    this.sprite.destroy();
    this.hpBar.destroy();
    this.moraleBadge?.destroy();
    this.moraleBadge = null;
  }
}

// ---- definitions -----------------------------------------------------------

// --- party roles ------------------------------------------------------------

export const VANGUARD_DEF: Omit<UnitDef, 'id'> = {
  name: 'Vanguard',
  spriteKey: 'sprite_player',
  faction: 'party',
  maxHp: 110,
  attackDamage: 14,
  attackRange: 1.5,
  attackInterval: 0.75,
  moveSpeed: 4.2,
  weapon: 'slash',
  role: 'vanguard',
  tint: 0xffe6a8,
  sightRange: 7,
};

export const MARKSMAN_DEF: Omit<UnitDef, 'id'> = {
  name: 'Marksman',
  spriteKey: 'sprite_player',
  faction: 'party',
  maxHp: 65,
  attackDamage: 11,
  attackRange: 5.5,
  attackInterval: 1.0,
  moveSpeed: 3.8,
  weapon: 'pierce',
  role: 'marksman',
  tint: 0x9ad6ff,
  sightRange: 9,
};

export const MEDIC_DEF: Omit<UnitDef, 'id'> = {
  name: 'Medic',
  spriteKey: 'sprite_player',
  faction: 'party',
  maxHp: 70,
  attackDamage: 6,
  attackRange: 4.0,
  attackInterval: 1.2,
  moveSpeed: 4.0,
  weapon: 'magic',
  role: 'medic',
  tint: 0xc4f4a8,
  sightRange: 8,
};

export const MAGE_DEF: Omit<UnitDef, 'id'> = {
  name: 'Mage',
  spriteKey: 'sprite_player',
  faction: 'party',
  maxHp: 55,
  attackDamage: 18,    // burst, glass-cannon
  attackRange: 6.0,
  attackInterval: 1.4,
  moveSpeed: 3.6,
  weapon: 'magic',
  role: 'mage',
  tint: 0xc89aff,      // purple
  sightRange: 10,
};

// Kept for backward compatibility — old code paths still import PLAYER_DEF.
export const PLAYER_DEF = VANGUARD_DEF;

export const ENEMY_DEFS: Record<string, Omit<UnitDef, 'id'>> = {
  goblin: {
    name: 'Goblin',
    spriteKey: 'sprite_goblin',
    faction: 'enemy',
    maxHp: 28,
    attackDamage: 6,
    attackRange: 1.5,
    attackInterval: 1.0,
    moveSpeed: 3.2,
    weapon: 'slash',
    sightRange: 7,
  },
  skeleton_archer: {
    name: 'Skeleton Archer',
    spriteKey: 'sprite_skeleton_archer',
    faction: 'enemy',
    maxHp: 22,
    attackDamage: 9,
    attackRange: 5.5,
    attackInterval: 1.4,
    moveSpeed: 2.6,
    weapon: 'pierce',
    sightRange: 9,
  },
  orc_brute: {
    name: 'Orc Brute',
    spriteKey: 'sprite_orc_brute',
    faction: 'enemy',
    maxHp: 60,
    attackDamage: 12,
    attackRange: 1.5,
    attackInterval: 1.4,
    moveSpeed: 2.4,
    weapon: 'bludgeon',
    sightRange: 8,
  },
};
